/**
 * Le plan de la carte : du calcul, pas de JSX (ticket 36 de finition-v1, repris au ticket 06
 * de retours-testeurs : la carte déplie sur place).
 *
 * Où se posent les étiquettes, la place qu'elles prennent, et où attend une bulle cachée. La
 * fiche d'une bulle dépliée trouve son fil ailleurs (`ficheParSonId.ts`). La carte n'a plus de coins ni de noms de part depuis le ticket 04
 * du chantier `priorites`. `VueTableau` n'a plus qu'à dessiner. Rien
 * n'est stocké : tout se recalcule depuis ce que le serveur envoie. Les places des bulles,
 * elles, viennent du serveur (`domain/carte/carte.ts`) : ce fichier n'en invente aucune.
 */
import type { CarteDeFeuille, TransversaleDeFeuille, VueDeCarte } from '../partage/api/types';
import { largeurEstimee } from './EtiquettesTransversales';
import type { Point } from './geometrieCarte';
import {
  HAUTEUR_ETIQUETTE,
  placerEtiquettes,
  type EtiquetteDuDehors,
} from './placeDesEtiquettes';

export interface DessinDeLaVue {
  etiquettes: EtiquetteDuDehors<TransversaleDeFeuille>[];
}

/** Les étiquettes des liens qui sortent de la feuille : elles bordent les bulles, sans les couvrir. */
export function dessinDeLaVue(
  vue: VueDeCarte,
  carte: Pick<CarteDeFeuille, 'hauteurs'>
): DessinDeLaVue {
  const etiquettes = placerEtiquettes(vue.dehors, vue.places, largeurEstimee, carte.hauteurs);
  return { etiquettes };
}

/**
 * La hauteur que les étiquettes doivent éviter : celle que le serveur a estimée,
 * ou celle mesurée à l'écran si le titre prend plus de place (ticket 25). Jamais moins que
 * l'estimation : les cartes déjà vérifiées gardent leurs étiquettes où elles étaient.
 */
export function hauteursAuMoins(
  estimees: Record<string, number>,
  mesurees: Record<string, { hauteur: number }>
): Record<string, number> {
  const hauteurs = { ...estimees };
  for (const [id, { hauteur }] of Object.entries(mesurees)) {
    if (hauteur > (hauteurs[id] ?? 0)) hauteurs[id] = hauteur;
  }
  return hauteurs;
}

/** De quoi respirer autour des bulles, au bord de la surface. */
const MARGE_X = 150;
const MARGE_Y = 60;
/** Une feuille vide : le titre seul, et la place qui attend autour. */
const TAILLE_VIDE = { largeur: 900, hauteur: 500 };

/**
 * L'encombrement de ce que la carte montre, avec ses étiquettes : la
 * caméra s'en sert pour cadrer au départ et pour savoir jusqu'où dézoomer.
 */
export function tailleDeLaVue(
  vue: VueDeCarte,
  dessin: DessinDeLaVue,
  vide: boolean
): { largeur: number; hauteur: number } {
  if (vide) return TAILLE_VIDE;
  const bord = etendue(dessin);
  return {
    largeur: Math.max(vue.largeur + 2 * MARGE_X, bord.largeur),
    hauteur: Math.max(vue.hauteur + 2 * MARGE_Y, bord.hauteur),
  };
}

/** La boîte, centrée sur le titre, qui contient les étiquettes. */
function etendue({ etiquettes }: DessinDeLaVue): { largeur: number; hauteur: number } {
  let demiLargeur = 0;
  let demiHauteur = 0;
  for (const { point, largeur } of etiquettes) {
    demiLargeur = Math.max(demiLargeur, Math.abs(point.x) + largeur / 2);
    demiHauteur = Math.max(demiHauteur, Math.abs(point.y) + HAUTEUR_ETIQUETTE / 2);
  }
  // De quoi respirer au bord de la surface.
  return { largeur: demiLargeur * 2 + 32, hauteur: demiHauteur * 2 + 24 };
}

/** Sa place, ou celle de la bulle visible qui la contient : une bulle cachée attend sur sa mère. */
export function placeDe(
  id: string,
  places: Readonly<Record<string, Point>>,
  parId: ReadonlyMap<string, { parentId: string | null }>,
  centreId: string
): Point {
  let courant: string | null | undefined = id;
  while (courant && courant !== centreId) {
    const place = places[courant];
    if (place) return place;
    courant = parId.get(courant)?.parentId;
  }
  return { x: 0, y: 0 };
}
